"use client";

import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import Image from "next/image";
import { useEffect, useState, useCallback, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { MoveRight } from 'lucide-react';
import traderNodeImg1 from "/public/images/walk-through/canvas/modal/1.png";
import traderNodeImg2 from "/public/images/walk-through/canvas/modal/2.png";
import traderNodeImg3 from "/public/images/walk-through/canvas/modal/3.png";
import traderNodeImg4 from "/public/images/walk-through/canvas/modal/4.png";
import traderNodeImg5 from "/public/images/walk-through/canvas/modal/5.png";

interface IStrategyCanvasModalProps {
  onClose: () => void;
}

export default function StrategyCanvasModal({
  onClose,
}: IStrategyCanvasModalProps) {
  const router = useRouter();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev === carouselImages.length - 1 ? 0 : prev + 1
    );
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isOpen) {
      interval = setInterval(nextImage, 5000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isOpen, nextImage]);

  const handleOpenChange = (open: boolean) => {
    onClose();
    setIsOpen(open);
    if (open) {
      setCurrentImageIndex(0);
    }
  };

  if (!isOpen) return null;
  return (
    <>
      <div className="fixed inset-0 z-50 flex  items-center justify-center bg-opacity-30 bg-[#66585858]">
        <div
          ref={modalRef}
          className="max-w-[90vw] sm:max-w-md md:max-w-lg lg:max-w-xl w-full bg-[#0B1120] text-white border rounded-lg shadow-xl"
        >
          <div className="relative">
            <button
              onClick={() => handleOpenChange(false)}
              className="absolute right-4 top-4  z-[9999] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4 text-gray-400" />
              <span className="sr-only">Close</span>
            </button>

            <div className="grid gap-6">
              <div className="relative h-[270px] w-full overflow-hidden">
                {carouselImages.map((image, index) => (
                  <Image
                    key={image.id}
                    src={image.url}
                    alt={image.alt}
                    fill
                    className={`w-full h-full  object-fill rounded-t-lg transition-opacity duration-300 ease-in-out ${currentImageIndex === index ? "opacity-100" : "opacity-0"
                      }`}
                    priority={index === 0}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${traderNodeImg1}`;
                    }}
                  />
                ))}

                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                  {carouselImages.map((_, index) => (
                    <button
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === index
                        ? "bg-white w-4"
                        : "bg-white/50"
                        }`}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              <div className="px-6 pb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Transforming analysis into action
                </h2>
                <p className="text-gray-400 text-sm">
                  Discover how Level2 empowers traders with a smarter,
                  data-driven approach, redefining technical analysis and
                  decision-making.
                </p>

                <div className="mt-6 text-xs">
                  <h3 className=" font-medium text-gray-400 mb-4">
                    Signup to access:
                  </h3>
                  <ul className="space-y-2 text-gray-400 ">
                    <li className="flex gap-2 items-start text-xs before:content-['•'] before:text-base before:mt-[-2px]">
                      <div>
                        <span className="font-semibold text-gray-200">
                          Visual Strategy Building:
                        </span>{" "}
                        <span className=" text-gray-400">
                          Design strategies effortlessly with our intuitive
                          interface.
                        </span>
                      </div>
                    </li>
                    <li className="flex gap-2 items-start text-xs before:content-['•'] before:text-base before:mt-[-2px]">
                      <div>
                        <span className="font-semibold text-gray-200">
                          Real-Time Backtesting:
                        </span>{" "}
                        <span className=" text-gray-400">
                          Test your ideas instantly with live market data.
                        </span>
                      </div>
                    </li>
                    <li className="flex gap-2 items-start text-xs before:content-['•'] before:text-base before:mt-[-2px]">
                      <div>
                        <span className="font-semibold text-gray-200">
                          Full Strategy Automation:
                        </span>{" "}
                        <span className=" text-gray-400">
                          Automate your trading strategies for seamless execution.
                        </span>
                      </div>
                    </li>
                    <li className="flex gap-2 items-start text-xs before:content-['•'] before:text-base before:mt-[-2px]">
                      <div>
                        <span className="font-semibold text-gray-200">
                          Virtual and Live Trading:
                        </span>{" "}
                        <span className=" text-gray-400">
                          Transition smoothly from practice to real-world trading.
                        </span>
                      </div>
                    </li>
                    <li className="flex gap-2 items-start text-xs before:content-['•'] before:text-base before:mt-[-2px]">
                      <div>
                        <span className="font-semibold text-gray-200">
                          Technical Analysis Scanners:
                        </span>{" "}
                        <span className=" text-gray-400">
                          Powerful tools to uncover trading opportunities.
                        </span>
                      </div>
                    </li>
                  </ul>

                  <div className="flex justify-center items-center">
                    <Button className=" mt-3 bg-blue-600 hover:bg-blue-700 text-white">
                      <Link
                        href={`/create-new-account?locale=${router.query.locale || "en"
                          }`}
                        target="_blank"
                      >
                        <div className="flex items-center">
                          <div>Sign up</div>
                          <div><MoveRight className="ml-2 w-[15px]" /></div>
                        </div>

                      </Link>
                    </Button>
                  </div>

                  <div className="flex items-center justify-center text-[.5rem] mt-3 text-center ">
                    <p className="text-gray-500 leading-normal">
                      This information does not contain - and should not be
                      construed as containing an investment recommendation or an
                      offer of or solicitation for transaction in any financial
                      instrument. Please see the information provided in our
                      disclaimer, our website or seek independent advice to help
                      inform your choices.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const carouselImages = [
  {
    id: 1,
    url: traderNodeImg1,
    alt: "Trading chart showing price analysis",
  },
  {
    id: 2,
    url: traderNodeImg2,
    alt: "Harmonic pattern ",
  },
  {
    id: 3,
    url: traderNodeImg3,
    alt: "Trading simulation overview",
  },
  {
    id: 4,
    url: traderNodeImg4,
    alt: "Trading trader overview",
  },
  {
    id: 3,
    url: traderNodeImg5,
    alt: "Results of trading",
  },
];
